import { PropsApiResponse } from './types';
import { getPropsLocalApiBase } from './leagueConfig';

const getBaseUrl = (): string => {
  return process.env.REACT_APP_DFS_MARKETING_API || 'https://localhost:7233';
};

export interface PropsApiParams {
  leagues: string[];
  state: string;
  sportsbooks: string[];
  accessToken?: string | null;
  stokWeight?: number;
  marketWeight?: number;
  /** React Query's cancellation signal — a superseded request must not resolve into Redux. */
  signal?: AbortSignal;
}

export interface PropsApiResult {
  success: boolean;
  data: PropsApiResponse | null;
  error?: string;
  timestamp: Date;
}

export async function fetchPropsData(params: PropsApiParams): Promise<PropsApiResult> {
  const timestamp = new Date();

  // Dev-only: serve the local NFL fixture when flagged. The env reads are inlined (not behind a helper) so
  // the bundler sees literal constants and drops this branch — and the require — from production builds.
  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.REACT_APP_PROPS_MOCK_NFL === 'true' &&
    params.leagues.some((l) => l.toUpperCase() === 'NFL')
  ) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { buildNflMockPropsResponse } = require('./mocks/nflMockProps') as typeof import('./mocks/nflMockProps');
    await new Promise((resolve) => setTimeout(resolve, 350)); // keep the loading state honest
    return { success: true, data: buildNflMockPropsResponse(params.state), timestamp };
  }

  try {
    // Default path: stokastic-site proxy (/api/props) which forwards to OddsShopper.MainApi.
    // Local dev path: hit a local MainApi directly with its internal API key.
    // A relative local base (e.g. /local-props-api) goes through the CRA dev-server proxy in
    // src/setupProxy.js, which attaches the API key server-side and avoids a CORS preflight.
    const localApiBase = getPropsLocalApiBase();
    const url = localApiBase
      ? new URL(`${localApiBase}/api/projections/props`, window.location.origin)
      : new URL(`${getBaseUrl()}/api/props`);

    params.leagues.forEach((league) => url.searchParams.append('leagues', league));
    url.searchParams.append('state', params.state);
    params.sportsbooks.forEach((book) => url.searchParams.append('sportsbooks', book));

    if (params.stokWeight !== undefined) {
      url.searchParams.append('StokWeight', params.stokWeight.toString());
    }
    if (params.marketWeight !== undefined) {
      url.searchParams.append('MarketWeight', params.marketWeight.toString());
    }

    // Local-API mode goes through the CRA dev proxy, which attaches the internal key server-side.
    const headers: HeadersInit = {};
    if (!localApiBase && params.accessToken) {
      headers['Authorization'] = `Bearer ${params.accessToken}`;
    }

    const response = await fetch(url.toString(), { headers, signal: params.signal });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[PropsAPI] API error:', response.status, errorText);
      return {
        success: false,
        data: null,
        error: `API returned ${response.status}`,
        timestamp,
      };
    }

    const data: PropsApiResponse = await response.json();

    return {
      success: true,
      data,
      timestamp,
    };
  } catch (error) {
    // A cancelled request is not a failure: rethrow so React Query drops it instead of us
    // reporting an error (or, worse, a stale board) for a query that no longer matters.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    console.error('[PropsAPI] Fetch failed:', error);
    return {
      success: false,
      data: null,
      error: error instanceof Error ? error.message : 'Unknown error',
      timestamp,
    };
  }
}
